import { useEffect, useState } from "react";
import { Activity, Clock, Database, AlertTriangle } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useApi } from "@/lib/api";
import { EpidemicMap } from "@/components/epidemic/EpidemicMap";
import { Panel, AgeChart, PathologyChart, CityChart, Timeline, SexSplit } from "@/components/epidemic/Charts";
import { Thresholds } from "@/components/epidemic/Thresholds";

const ALL = "__all";
const Filter = ({ id, value, onChange, options, placeholder }) => (
  <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
    <SelectTrigger className="w-full sm:w-52 bg-white" data-testid={id}><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value={ALL}>{placeholder}</SelectItem>
      {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
    </SelectContent>
  </Select>
);

const Kpi = ({ icon: Icon, label, value, id }) => (
  <div className="bg-white border border-stone-200 rounded-lg p-5" data-testid={`epi-kpi-${id}`}>
    <div className="flex items-center justify-between"><p className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500">{label}</p><Icon className="h-4 w-4 text-[#C2410C]" /></div>
    <p className="font-heading text-3xl font-black text-stone-900 mt-3" data-testid={`epi-kpi-${id}-value`}>{value}</p>
  </div>
);

export const EpidemicDashboard = () => {
  const api = useApi();
  const [meta, setMeta] = useState({ pathologies: [], wilayas: [] });
  const [f, setF] = useState({ pathology: "", wilaya: "", gender: "", days: "30", include_demo: true });
  const [s, setS] = useState(null);
  useEffect(() => { api("get", "/epidemic/meta").then(setMeta); }, [api]);
  useEffect(() => {
    const q = new URLSearchParams(Object.entries(f).filter(([, v]) => v !== "").map(([k, v]) => [k, String(v)])).toString();
    api("get", `/epidemic/stats?${q}`).then(setS);
  }, [api, f]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const wilayaCode = (name) => meta.wilayas.find((w) => w.name === name)?.code || "";

  return (
    <div className="space-y-6" data-testid="epidemic-dashboard">
      <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:items-center">
        <Filter id="epi-filter-pathology" value={f.pathology} onChange={set("pathology")} placeholder="Toutes pathologies" options={meta.pathologies.map((p) => [p, p])} />
        <Filter id="epi-filter-wilaya" value={f.wilaya} onChange={set("wilaya")} placeholder="Toutes les wilayas" options={meta.wilayas.map((w) => [w.code, `${w.code} · ${w.name}`])} />
        <Filter id="epi-filter-gender" value={f.gender} onChange={set("gender")} placeholder="Filles et garçons" options={[["F", "Filles"], ["M", "Garçons"]]} />
        <Filter id="epi-filter-period" value={f.days === "0" ? "" : f.days} onChange={(v) => set("days")(v || "0")} placeholder="Toute la période" options={[["7", "7 derniers jours"], ["30", "30 derniers jours"], ["90", "90 derniers jours"], ["365", "12 derniers mois"]]} />
        <label className="flex items-center gap-2 text-sm text-stone-700"><Switch checked={f.include_demo} onCheckedChange={set("include_demo")} data-testid="epi-filter-demo" /> Inclure les données de démonstration</label>
      </div>
      {!s ? <p className="text-sm text-stone-500">Chargement…</p> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi icon={Activity} id="total" label="Cas" value={s.total} />
            <Kpi icon={Clock} id="week" label="7 derniers jours" value={s.last_7_days} />
            <Kpi icon={AlertTriangle} id="alerts" label="Seuils dépassés" value={s.thresholds.filter((t) => t.level === "depasse").length} />
            <Kpi icon={Database} id="demo" label="Déclarations démo" value={`${s.demo_share} / ${s.records}`} />
          </div>
          <div className="grid lg:grid-cols-3 gap-6">
            <Panel title="Carte des cas (par commune)" testId="epi-panel-map" className="lg:col-span-2">
              <EpidemicMap cities={s.points} />
              <p className="text-xs text-stone-500 mt-2">Taille du cercle = nombre de cas, couleur = pathologie dominante.</p>
            </Panel>
            <Panel title="Répartition par wilaya" testId="epi-panel-wilaya"><CityChart data={s.by_wilaya.slice(0, 12).map((w) => ({ city: w.name, count: w.count }))} onSelect={(n) => set("wilaya")(wilayaCode(n))} /></Panel>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <Panel title="Répartition par âge" testId="epi-panel-age"><AgeChart data={s.by_age} /></Panel>
            <Panel title="Répartition par pathologie" testId="epi-panel-pathology"><PathologyChart data={s.by_pathology.slice(0, 12)} /></Panel>
          </div>
          <div className="grid lg:grid-cols-3 gap-6">
            <Panel title="Évolution hebdomadaire" testId="epi-panel-timeline" className="lg:col-span-2"><Timeline data={s.timeline.map((t) => ({ ...t, confirme: undefined }))} /></Panel>
            <Panel title="Répartition par sexe" testId="epi-panel-sex"><SexSplit data={s.by_gender} /></Panel>
          </div>
          <Thresholds items={s.thresholds} />
        </>
      )}
    </div>
  );
};
